import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import apiService from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';

interface GCalSettings {
  calendarId: string;
  impersonateAs: string | null;
  serviceAccountEmail: string | null;
  serviceAccountProjectId: string | null;
  serviceAccountClientId: string | null;
  isReady: boolean;
  lastError: string | null;
  source: 'settings_file' | 'env' | 'default';
  envFallback: string | null;
  secondaryCalendarId: string | null;
  secondarySource: 'settings_file' | 'env' | 'unset';
  secondaryEnvFallback: string | null;
}

const GoogleCalendarSettings: React.FC = () => {
  const { addToast } = useToast();
  const [settings, setSettings] = useState<GCalSettings | null>(null);
  const [calendarId, setCalendarId] = useState('');
  const [impersonateAs, setImpersonateAs] = useState('');
  const [secondaryCalendarId, setSecondaryCalendarId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSecondary, setSavingSecondary] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingSecondary, setTestingSecondary] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [uploadingSA, setUploadingSA] = useState(false);
  const saFileInputRef = useRef<HTMLInputElement | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const resp = await apiService.get<GCalSettings>('/settings/google-calendar');
      if (resp.data?.success) {
        setSettings(resp.data.data);
        setCalendarId(resp.data.data.calendarId);
        setImpersonateAs(resp.data.data.impersonateAs || '');
        setSecondaryCalendarId(resp.data.data.secondaryCalendarId || '');
      } else {
        addToast(resp.data?.message || 'Konnte Google-Calendar-Einstellungen nicht laden.', 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || 'Konnte Google-Calendar-Einstellungen nicht laden.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const handleSave = async () => {
    if (!calendarId.trim()) {
      addToast('Calendar-ID darf nicht leer sein.', 'error');
      return;
    }
    setSaving(true);
    try {
      const resp = await apiService.put<GCalSettings>('/settings/google-calendar', {
        calendarId: calendarId.trim(),
        impersonateAs: impersonateAs.trim(),
      });
      if (resp.data?.success) {
        setSettings(resp.data.data);
        setImpersonateAs(resp.data.data.impersonateAs || '');
        addToast('Einstellungen gespeichert.', 'success');
      } else {
        addToast(resp.data?.message || 'Speichern fehlgeschlagen.', 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || 'Speichern fehlgeschlagen.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const resp = await apiService.post<any>('/settings/google-calendar/test', {});
      if (resp.data?.success) {
        const d = resp.data.data;
        addToast(`✅ Zugriff OK — "${d?.summary || 'Calendar'}" (${d?.timeZone || '?'})`, 'success');
      } else {
        addToast(resp.data?.message || 'Test fehlgeschlagen.', 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || 'Test fehlgeschlagen.', 'error');
    } finally {
      setTesting(false);
    }
  };

  // Speichert NUR die secondary calendar ID — getrennt vom Hauptkalender-Save,
  // damit ein Versehen im Hauptkalender-Feld nichts am Bisherigen verändert.
  const handleSaveSecondary = async () => {
    setSavingSecondary(true);
    try {
      const resp = await apiService.put<GCalSettings>('/settings/google-calendar', {
        secondaryCalendarId: secondaryCalendarId.trim(),
      });
      if (resp.data?.success) {
        setSettings(resp.data.data);
        setSecondaryCalendarId(resp.data.data.secondaryCalendarId || '');
        addToast(
          resp.data.data.secondaryCalendarId
            ? `✅ Neuer Hauptkalender gespeichert: ${resp.data.data.secondaryCalendarId}`
            : 'Neuer Hauptkalender entfernt.',
          'success'
        );
      } else {
        addToast(resp.data?.message || 'Speichern fehlgeschlagen.', 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || 'Speichern fehlgeschlagen.', 'error');
    } finally {
      setSavingSecondary(false);
    }
  };

  const handleTestSecondary = async () => {
    setTestingSecondary(true);
    try {
      const resp = await apiService.post<any>('/settings/google-calendar/test', { target: 'secondary' });
      if (resp.data?.success) {
        const d = resp.data.data;
        addToast(`✅ Zugriff OK — "${d?.summary || 'Calendar'}" (${d?.timeZone || '?'})`, 'success');
      } else {
        addToast(resp.data?.message || 'Test fehlgeschlagen.', 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || 'Test fehlgeschlagen.', 'error');
    } finally {
      setTestingSecondary(false);
    }
  };

  const handleServiceAccountUpload = async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.json')) {
      addToast('Bitte eine .json-Datei wählen (Google Service-Account Key).', 'error');
      return;
    }
    if (file.size > 32 * 1024) {
      addToast('Datei zu groß — Service-Account-JSONs sind üblicherweise unter 5 KB.', 'error');
      return;
    }
    if (!window.confirm(
      `Service-Account ersetzen mit "${file.name}"?\n\n` +
      `Die aktuelle Datei wird als service-account.json.bak gesichert. ` +
      `Schlägt die Authentifizierung mit dem neuen Account fehl, wird automatisch zurückgerollt.`
    )) return;

    setUploadingSA(true);
    try {
      const fd = new FormData();
      fd.append('serviceAccount', file);
      // Bypass apiService here: its axios instance forces Content-Type: application/json,
      // which prevents the browser from setting the multipart boundary header for FormData.
      const token = localStorage.getItem('admin_token');
      const base = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api';
      const resp = await axios.post<{ success: boolean; data: GCalSettings; message?: string }>(
        `${base}/settings/google-calendar/service-account`,
        fd,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (resp.data?.success) {
        setSettings(resp.data.data);
        setCalendarId(resp.data.data.calendarId);
        addToast(`✅ Service-Account aktiv: ${resp.data.data.serviceAccountEmail}`, 'success');
      } else {
        addToast(resp.data?.message || 'Upload fehlgeschlagen.', 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || 'Upload fehlgeschlagen.', 'error');
    } finally {
      setUploadingSA(false);
      if (saFileInputRef.current) saFileInputRef.current.value = '';
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Persistierte Calendar-ID löschen und auf Default (env/Service-Account) zurückfallen?')) return;
    setResetting(true);
    try {
      const resp = await apiService.delete<GCalSettings>('/settings/google-calendar');
      if (resp.data?.success) {
        setSettings(resp.data.data);
        setCalendarId(resp.data.data.calendarId);
        addToast('Auf Default zurückgesetzt.', 'success');
      } else {
        addToast(resp.data?.message || 'Reset fehlgeschlagen.', 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || 'Reset fehlgeschlagen.', 'error');
    } finally {
      setResetting(false);
    }
  };

  if (loading) return <div className="bg-white rounded-lg shadow p-6 text-gray-500">Lade Google-Calendar-Einstellungen…</div>;
  if (!settings) return null;

  const sourceLabel = {
    settings_file: 'Persistiert (UI gespeichert)',
    env: 'Aus Umgebungsvariable',
    default: 'Default (Service-Account-Kalender)',
  }[settings.source];

  return (
    <section className="bg-white rounded-lg shadow p-6 space-y-6">
      <div>
        <h2 className="text-lg font-bold text-gray-900">📅 Google Calendar</h2>
        <p className="text-sm text-gray-600 mt-1">
          Steuert, mit welchem Service-Account und in welchen Kalender Termine geschrieben werden.
        </p>
      </div>

      {/* Status row */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
          settings.isReady ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'
        }`}>
          {settings.isReady ? '● Verbunden' : '● Nicht verbunden'}
        </span>
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
          Quelle: {sourceLabel}
        </span>
        {settings.lastError && (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
            ⚠️ {settings.lastError}
          </span>
        )}
      </div>

      {/* Service account — display + upload */}
      <div className="border border-gray-200 rounded p-4 space-y-3 bg-gray-50">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Aktiver Service-Account</label>
            <input
              type="text"
              value={settings.serviceAccountEmail || '(unbekannt)'}
              readOnly
              className="w-full p-2 border border-gray-200 bg-white rounded text-sm text-gray-700 font-mono"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 text-[11px] text-gray-600">
              <div>
                <span className="font-semibold text-gray-500">Projekt:</span>{' '}
                <span className="font-mono">{settings.serviceAccountProjectId || '–'}</span>
              </div>
              <div>
                <span className="font-semibold text-gray-500">Client-ID:</span>{' '}
                <span className="font-mono">{settings.serviceAccountClientId || '–'}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-3 border-t border-gray-200">
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Service-Account austauschen</label>
          <p className="text-[11px] text-gray-500 mb-2">
            JSON-Schlüsseldatei aus der Google Cloud Console hochladen (IAM &amp; Admin → Service Accounts → Keys → „Add key" → JSON).
            Die vorherige Datei wird automatisch als <code>service-account.json.bak</code> gesichert; bei fehlgeschlagener Authentifizierung wird zurückgerollt.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={saFileInputRef}
              type="file"
              accept="application/json,.json"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleServiceAccountUpload(f);
              }}
              disabled={uploadingSA}
              className="text-xs text-gray-700 file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-light-blue file:text-white hover:file:bg-opacity-90 file:cursor-pointer disabled:opacity-50"
            />
            {uploadingSA && <span className="text-xs text-gray-500">Lade hoch &amp; authentifiziere…</span>}
          </div>
          <p className="text-[11px] text-amber-700 mt-2">
            ⚠️ Zwei Wege, damit der Account auf einen Kalender zugreifen kann:
            (1) Kalender direkt für die Adresse oben als <strong>„Änderungen an Terminen vornehmen"</strong> freigeben, oder
            (2) <strong>Domain-Wide Delegation</strong> einrichten und im Feld <em>„Impersonate als"</em> unten den Workspace-User eintragen, in dessen Namen der Account handeln soll.
          </p>
        </div>
      </div>

      {/* NEW: secondary calendar — this is the new default target for new
          appointments. Rendered FIRST and prominently so admins see it as the
          primary configuration surface. */}
      <div className="border-2 border-green-200 bg-green-50/40 rounded p-4 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <label className="block text-xs font-bold text-green-900 uppercase tracking-wider">
            Neuer Hauptkalender <span className="font-normal lowercase text-green-700">(Standard für neue Termine)</span>
          </label>
          {settings.secondaryCalendarId && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-green-600 text-white">
              AKTIV
            </span>
          )}
        </div>
        <input
          type="text"
          value={secondaryCalendarId}
          onChange={(e) => setSecondaryCalendarId(e.target.value)}
          placeholder="info@seadent.es"
          className="w-full p-2 border border-green-300 rounded text-sm font-mono bg-white focus:ring-2 focus:ring-green-500 outline-none"
        />
        <p className="text-[11px] text-green-900">
          Neue Termine landen ab Speichern in diesem Kalender. Voraussetzung: der Service-Account
          <code className="bg-white px-1 mx-1 rounded font-mono text-[10px]">{settings.serviceAccountEmail || '…'}</code>
          ist in den Freigabe-Einstellungen mit Rolle „Änderungen an Terminen vornehmen" hinzugefügt.
          Leer lassen → der bisherige Kalender unten bleibt Default.
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          <button
            onClick={handleTestSecondary}
            disabled={testingSecondary || !settings.isReady || !settings.secondaryCalendarId}
            className="px-3 py-1.5 bg-white border border-green-600 text-green-800 text-sm rounded font-semibold disabled:opacity-40 hover:bg-green-100"
            title={!settings.secondaryCalendarId ? 'Erst speichern, dann testbar' : ''}
          >
            {testingSecondary ? 'Teste…' : 'Verbindung testen'}
          </button>
          <button
            onClick={handleSaveSecondary}
            disabled={savingSecondary || secondaryCalendarId.trim() === (settings.secondaryCalendarId || '')}
            className="px-4 py-1.5 bg-green-700 text-white text-sm rounded font-semibold disabled:opacity-50 hover:bg-green-800"
          >
            {savingSecondary ? 'Speichere…' : (secondaryCalendarId.trim() ? 'Speichern' : 'Entfernen')}
          </button>
        </div>
        {settings.secondaryEnvFallback && settings.secondarySource !== 'env' && (
          <p className="text-[11px] text-amber-700">
            ℹ️ Env-Fallback verfügbar: <code className="bg-amber-50 px-1 rounded">{settings.secondaryEnvFallback}</code>
          </p>
        )}
      </div>

      {/* Calendar ID input — BISHERIGER Kalender. Bleibt unverändert, nur
          re-labeled, damit Admins wissen welcher welcher ist. */}
      <div className="border border-gray-200 rounded p-4 space-y-2">
        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">
          Bisheriger Kalender <span className="font-normal lowercase text-gray-400">(Bestandstermine, nur auf Anfrage Default)</span>
        </label>
        <input
          type="text"
          value={calendarId}
          onChange={(e) => setCalendarId(e.target.value)}
          placeholder="xxxxxx@group.calendar.google.com"
          className="w-full p-2 border border-gray-300 rounded text-sm font-mono focus:ring-2 focus:ring-light-blue outline-none"
        />
        <p className="text-[11px] text-gray-500">
          Bestehende Termine bleiben in diesem Kalender. Neue Termine landen hier nur, wenn beim Anlegen
          „Bisheriger Kalender" gewählt wird — oder wenn oben kein Hauptkalender konfiguriert ist.
          ⚠️ Änderungen hier können bestehende Termin-Syncs brechen.
        </p>
        {settings.envFallback && settings.source !== 'env' && (
          <p className="text-[11px] text-amber-700">
            ℹ️ Env-Fallback verfügbar: <code className="bg-amber-50 px-1 rounded">{settings.envFallback}</code> (greift nach Reset auf Default).
          </p>
        )}
      </div>

      {/* Impersonate-As (Domain-Wide Delegation) */}
      <div>
        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
          Impersonate als <span className="font-normal lowercase text-gray-400">(optional, Domain-Wide Delegation)</span>
        </label>
        <input
          type="email"
          value={impersonateAs}
          onChange={(e) => setImpersonateAs(e.target.value)}
          placeholder="leer lassen für Standard — oder z.B. smile@seadentpalma.com"
          className="w-full p-2 border border-gray-300 rounded text-sm font-mono focus:ring-2 focus:ring-light-blue outline-none"
        />
        <p className="text-[11px] text-gray-500 mt-1">
          Wenn deine Google Workspace-Org externes Calendar-Sharing blockt, hier den Workspace-Nutzer eintragen,
          in dessen Namen der Service-Account handeln soll. Voraussetzung: In <code>admin.google.com → Sicherheit → API-Steuerung → Domainweite Delegierung</code>
          ist die Client-ID <code>{settings.serviceAccountClientId || '…'}</code> mit den Scopes <code>auth/calendar</code> und <code>auth/calendar.events</code> autorisiert.
        </p>
        {settings.impersonateAs && (
          <p className="text-[11px] text-green-700 mt-1">
            ✓ Aktiv impersoniert: <code className="bg-green-50 px-1 rounded">{settings.impersonateAs}</code>
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-gray-100">
        <button
          onClick={handleReset}
          disabled={resetting || settings.source !== 'settings_file'}
          className="text-sm text-gray-600 hover:text-red-700 underline disabled:no-underline disabled:opacity-40 disabled:cursor-not-allowed"
          title={settings.source !== 'settings_file' ? 'Aktuell nicht persistiert — kein Reset nötig' : 'Auf env/Default zurückfallen'}
        >
          {resetting ? 'Setze zurück…' : 'Auf Default zurücksetzen'}
        </button>

        <div className="flex gap-2">
          <button
            onClick={handleTest}
            disabled={testing || !settings.isReady}
            className="px-4 py-2 bg-light-blue text-white rounded font-semibold disabled:opacity-50 hover:bg-opacity-90"
            title={!settings.isReady ? 'Service nicht initialisiert' : ''}
          >
            {testing ? 'Teste…' : 'Verbindung testen'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || (calendarId.trim() === settings.calendarId && impersonateAs.trim() === (settings.impersonateAs || ''))}
            className="px-5 py-2 bg-brand-primary text-white rounded font-semibold disabled:opacity-50 hover:bg-opacity-90"
            title="Speichert nur den bisherigen Kalender + Impersonation. Der neue Hauptkalender hat oben einen eigenen Speichern-Button."
          >
            {saving ? 'Speichere…' : 'Bisherigen speichern'}
          </button>
        </div>
      </div>
    </section>
  );
};

export default GoogleCalendarSettings;
